/* eslint-disable default-param-last */
import { Reducer } from 'redux';
import produce from 'immer';
import tabId from 'services/tabId';
import {
  BidderConsiderationTeamRow,
  BiddingSummaryTypes,
  BiddingSummaryState,
  BiddingSummaryBidderConsideration,
} from './types';

const DEFAULT_REQUEST_STATE = {
  loading: false,
  loaded: false,
  error: false,
};

const INITIAL_STATE: BiddingSummaryState = {
  bidding_summary: {
    current_bidding_summary: {},
    bidding_summary_data: {},
    bidder_consideration_data: {},
    bidder_consideration_team_data: {},
  },
  request: {
    fetch_bidding_summary: {},
    fetch_bidder_consideration: {},
    fetch_bidder_consideration_team: {},
    save_bidder_consideration: {},
    delete_bidder_consideration: {},
  },
};

const ensureRequestCollections = (draft: BiddingSummaryState) => {
  if (!draft.request.fetch_bidding_summary) draft.request.fetch_bidding_summary = {};
  if (!draft.request.fetch_bidder_consideration) draft.request.fetch_bidder_consideration = {};
  if (!draft.request.fetch_bidder_consideration_team)
    draft.request.fetch_bidder_consideration_team = {};
  if (!draft.request.save_bidder_consideration) draft.request.save_bidder_consideration = {};
  if (!draft.request.delete_bidder_consideration) draft.request.delete_bidder_consideration = {};
};

const sameOpenBidding = (state: BiddingSummaryState, biddingId: number | undefined): boolean => {
  if (biddingId === undefined) return false;
  const openId = state.bidding_summary.current_bidding_summary[tabId()]?.currentBiddingId;
  if (openId === undefined || openId === null) return false;
  return String(openId) === String(biddingId);
};

/** Limpa cache e filas de request só de considerações do licitante para a aba atual. */
const clearBidderConsiderationStateForTab = (draft: BiddingSummaryState) => {
  const t = tabId();
  delete draft.bidding_summary.bidder_consideration_data[t];
  delete draft.bidding_summary.bidder_consideration_team_data[t];
  draft.request.fetch_bidder_consideration[t] = { ...DEFAULT_REQUEST_STATE };
  draft.request.fetch_bidder_consideration_team[t] = { ...DEFAULT_REQUEST_STATE };
  draft.request.save_bidder_consideration[t] = { ...DEFAULT_REQUEST_STATE };
  draft.request.delete_bidder_consideration[t] = { ...DEFAULT_REQUEST_STATE };
};

const reducer: Reducer<BiddingSummaryState> = (
  state = INITIAL_STATE,
  { type, result, biddingId, editalDownload, capagInfo },
) => {
  return produce(state, (draft) => {
    ensureRequestCollections(draft);
    if (!draft.bidding_summary.bidder_consideration_team_data) {
      draft.bidding_summary.bidder_consideration_team_data = {};
    }

    switch (type) {
      case BiddingSummaryTypes.OPEN_BIDDING_SUMMARY: {
        const previousSummary = state.bidding_summary.current_bidding_summary[tabId()];
        const shouldPreserveMetadata = previousSummary?.currentBiddingId === biddingId;
        const prevBiddingId = previousSummary?.currentBiddingId;
        if (prevBiddingId != null && prevBiddingId !== biddingId) {
          clearBidderConsiderationStateForTab(draft);
        }

        draft.bidding_summary.current_bidding_summary[tabId()] = {
          ...previousSummary,
          currentBiddingId: biddingId,
          editalDownload:
            editalDownload ??
            (shouldPreserveMetadata ? previousSummary?.editalDownload : undefined),
          capagInfo: capagInfo ?? (shouldPreserveMetadata ? previousSummary?.capagInfo : undefined),
          modalOpened: true,
        };
        break;
      }

      case BiddingSummaryTypes.FOCUS_BIDDING_SUMMARY: {
        const previousSummary = state.bidding_summary.current_bidding_summary[tabId()];
        const shouldPreserveMetadata = previousSummary?.currentBiddingId === biddingId;
        const prevBiddingId = previousSummary?.currentBiddingId;
        if (prevBiddingId != null && prevBiddingId !== biddingId) {
          delete draft.bidding_summary.bidding_summary_data[tabId()];
          clearBidderConsiderationStateForTab(draft);
        }

        draft.bidding_summary.current_bidding_summary[tabId()] = {
          ...previousSummary,
          currentBiddingId: biddingId,
          editalDownload: shouldPreserveMetadata ? previousSummary?.editalDownload : undefined,
          capagInfo: shouldPreserveMetadata ? previousSummary?.capagInfo : undefined,
          modalOpened: previousSummary?.modalOpened ?? false,
        };
        break;
      }

      case BiddingSummaryTypes.CLOSE_BIDDING_SUMMARY:
        draft.bidding_summary.current_bidding_summary[tabId()] = {
          ...state.bidding_summary.current_bidding_summary[tabId()],
          modalOpened: false,
        };
        break;

      case BiddingSummaryTypes.CLEAR_BIDDING_SUMMARY_DATA:
        delete draft.bidding_summary.bidding_summary_data[tabId()];
        clearBidderConsiderationStateForTab(draft);
        draft.request.fetch_bidding_summary[tabId()] = { ...DEFAULT_REQUEST_STATE };
        break;

      case BiddingSummaryTypes.CLEAR_CURRENT_BIDDING_SUMMARY:
        delete draft.bidding_summary.bidding_summary_data[tabId()];
        clearBidderConsiderationStateForTab(draft);
        draft.request.fetch_bidding_summary[tabId()] = { ...DEFAULT_REQUEST_STATE };
        break;

      case BiddingSummaryTypes.FETCH_BIDDING_SUMMARY_REQUEST:
        draft.request.fetch_bidding_summary[tabId()] = {
          ...DEFAULT_REQUEST_STATE,
          loading: true,
        };
        break;

      case BiddingSummaryTypes.FETCH_BIDDING_SUMMARY_SUCCESS: {
        if (sameOpenBidding(state, biddingId)) {
          draft.bidding_summary.bidding_summary_data[tabId()] = result.data;
          draft.request.fetch_bidding_summary[tabId()] = {
            ...DEFAULT_REQUEST_STATE,
            loaded: true,
          };
        }
        break;
      }

      case BiddingSummaryTypes.FETCH_BIDDING_SUMMARY_FAILURE:
        draft.request.fetch_bidding_summary[tabId()] = {
          ...DEFAULT_REQUEST_STATE,
          error: true,
        };
        break;

      case BiddingSummaryTypes.FETCH_BIDDER_CONSIDERATION_REQUEST:
        draft.request.fetch_bidder_consideration[tabId()] = {
          ...DEFAULT_REQUEST_STATE,
          loading: true,
        };
        break;

      case BiddingSummaryTypes.FETCH_BIDDER_CONSIDERATION_SUCCESS: {
        draft.request.fetch_bidder_consideration[tabId()] = {
          ...DEFAULT_REQUEST_STATE,
          loaded: true,
        };
        if (sameOpenBidding(state, biddingId)) {
          draft.bidding_summary.bidder_consideration_data[tabId()] = (result?.data ??
            null) as BiddingSummaryBidderConsideration | null;
        }
        break;
      }

      case BiddingSummaryTypes.FETCH_BIDDER_CONSIDERATION_FAILURE:
        draft.request.fetch_bidder_consideration[tabId()] = {
          ...DEFAULT_REQUEST_STATE,
          error: true,
        };
        break;

      case BiddingSummaryTypes.FETCH_BIDDER_CONSIDERATION_TEAM_REQUEST:
        draft.request.fetch_bidder_consideration_team[tabId()] = {
          ...DEFAULT_REQUEST_STATE,
          loading: true,
        };
        break;

      case BiddingSummaryTypes.FETCH_BIDDER_CONSIDERATION_TEAM_SUCCESS: {
        draft.request.fetch_bidder_consideration_team[tabId()] = {
          ...DEFAULT_REQUEST_STATE,
          loaded: true,
        };
        if (sameOpenBidding(state, biddingId)) {
          draft.bidding_summary.bidder_consideration_team_data[tabId()] = (result?.data ??
            []) as BidderConsiderationTeamRow[];
        }
        break;
      }

      case BiddingSummaryTypes.FETCH_BIDDER_CONSIDERATION_TEAM_FAILURE:
        draft.request.fetch_bidder_consideration_team[tabId()] = {
          ...DEFAULT_REQUEST_STATE,
          error: true,
        };
        break;

      case BiddingSummaryTypes.SAVE_BIDDER_CONSIDERATION_REQUEST:
        draft.request.save_bidder_consideration[tabId()] = {
          ...DEFAULT_REQUEST_STATE,
          loading: true,
        };
        break;

      case BiddingSummaryTypes.SAVE_BIDDER_CONSIDERATION_SUCCESS: {
        draft.request.save_bidder_consideration[tabId()] = {
          ...DEFAULT_REQUEST_STATE,
          loaded: true,
        };
        if (sameOpenBidding(state, biddingId)) {
          draft.bidding_summary.bidder_consideration_data[tabId()] = (result?.data ??
            null) as BiddingSummaryBidderConsideration | null;
        }
        break;
      }

      case BiddingSummaryTypes.SAVE_BIDDER_CONSIDERATION_FAILURE:
        draft.request.save_bidder_consideration[tabId()] = {
          ...DEFAULT_REQUEST_STATE,
          error: true,
        };
        break;

      case BiddingSummaryTypes.DELETE_BIDDER_CONSIDERATION_REQUEST:
        draft.request.delete_bidder_consideration[tabId()] = {
          ...DEFAULT_REQUEST_STATE,
          loading: true,
        };
        break;

      case BiddingSummaryTypes.DELETE_BIDDER_CONSIDERATION_SUCCESS: {
        draft.request.delete_bidder_consideration[tabId()] = {
          ...DEFAULT_REQUEST_STATE,
          loaded: true,
        };
        if (sameOpenBidding(state, biddingId)) {
          draft.bidding_summary.bidder_consideration_data[tabId()] = null;
          // Lista 'team' não é limpa: remove só o registro do contato atual; colegas inalterados.
        }
        break;
      }

      case BiddingSummaryTypes.DELETE_BIDDER_CONSIDERATION_FAILURE:
        draft.request.delete_bidder_consideration[tabId()] = {
          ...DEFAULT_REQUEST_STATE,
          error: true,
        };
        break;

      default:
        break;
    }
  });
};

export default reducer;
